import type { ComboboxOption } from '../combobox/ComboboxBase.js'
import { nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import comboboxStyle from '../combobox/Combobox.css'
import ComboboxBase, { retagComboboxStyles } from '../combobox/ComboboxBase.js'
import { ComboboxClearEvent } from '../combobox/ComboboxEvents.js'
import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import { LocalizeController } from '../localization/LocalizeController.js'

export type { ComboboxOption } from '../combobox/ComboboxBase.js'
export { ComboboxClearEvent } from '../combobox/ComboboxEvents.js'

/**
 * The shared combobox stylesheet is authored once against the `nord-combobox`
 * tag (see `Combobox.css`); re-prefix it for `nord-autocomplete` so this sibling
 * light-DOM control reuses the exact same `.n-*` rules without duplicating them.
 */
const style = retagComboboxStyles(comboboxStyle, 'nord-autocomplete')

/**
 * Autocomplete is a free-text input with suggestions: the text the user types
 * is itself the committed `value` (a string), and the options are completion
 * aids. Picking a suggestion fills the input with the option's `label` and commits
 * that label. Use it when the answer is open-ended text but you can offer
 * helpful completions (a city, a tag, a username); reach for
 * [Combobox](/components/combobox/) instead when the value must be one of a
 * fixed set of options.
 *
 * Autocomplete is a control only — it has no built-in label, hint or error.
 * Compose it inside a [Field](/components/field/) to give it a label, hint and
 * error message, the same way you would a native input.
 *
 * Base UI parity: Autocomplete is the Combobox primitive with
 * `selectionMode="none"` + `fillInputOnItemPress`.
 *
 * @status draft
 * @category form
 * @lightDom true
 *
 * @slot start - Optional slot used to place an icon or prefix at the start of the control.
 * @slot end - Optional slot used to place an icon or suffix at the end of the control.
 *
 * @fires {NordEvent} change - Dispatched when the committed value changes via user interaction.
 * @fires {NordEvent} input - Dispatched as the user types into the input.
 * @fires {NordEvent} open - Dispatched when the listbox is opened.
 * @fires {NordEvent} close - Dispatched when the listbox is closed.
 * @fires {ComboboxClearEvent} clear - Dispatched when the value is cleared via the clear button or `clear()`.
 *
 * @localization clearLabel - Accessible label for the clear button.
 * @localization noResults - Message shown when no options match the query.
 *
 * @cssprop [--n-combobox-list-inline-size] - Controls the inline size, or width, of the dropdown list. Defaults to the width of the control.
 * @cssprop [--n-combobox-block-size] - Controls the block size, or height, of the control. The medium default is `var(--n-space-xl)`; the s and l sizes scale from it.
 *
 * @attr {boolean} data-invalid - Present while the control is invalid (mirrors the `invalid` property). Style hook for the whole control.
 * @attr {boolean} data-focused - Present while the control has focus. Style hook for focus-within treatments.
 */
@customElement('nord-autocomplete')
export default class Autocomplete extends ComboboxBase {
  /**
   * The value of the autocomplete. This is the free text the user has typed (or
   * the `label` of a picked suggestion) — always a single string.
   */
  @property({ attribute: false }) value = ''

  /**
   * Enables inline ghost-text completion. While an option is highlighted with
   * the keyboard, the input shows the typed query followed by the remainder of
   * the highlighted option's label, with that remainder text-selected so
   * continued typing overwrites it. Accepting (Enter, ArrowRight/End at the end
   * of the text, or an option click) commits the full label.
   */
  @property({ reflect: true, type: Boolean }) inline = false

  /**
   * Shows a clear (✕) button while there is text. Pressing it resets the value
   * to `''`, then fires `clear` and `change`.
   */
  @property({ reflect: true, type: Boolean }) clearable = false

  /**
   * Whether clicking the input opens the suggestion list. Defaults to `false`
   * for autocomplete — matching Base UI's `openOnInputClick={false}`, the list
   * appears as the user types rather than on click. Set to `true` to also open
   * on click.
   */
  @property({ reflect: true, type: Boolean, attribute: 'open-on-input-click' }) override openOnInputClick = false

  protected override localize = new LocalizeController<'nord-autocomplete'>(this)

  /**
   * Whether the active option last changed via the keyboard (arrows/Home/End)
   * rather than pointer hover. Inline ghost completion only fills on keyboard
   * navigation, mirroring Base UI's `keyboardActiveRef`. Reactive so toggling it
   * (even when `activeIndex` is unchanged, e.g. a single filtered option)
   * re-renders the ghost.
   */
  @state() private keyboardActive = false

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)
  }

  /**
   * The committed free-text `value` *is* the query, so a programmatically-set
   * `value` (e.g. `el.value = 'fea'`) filters the open list without the user
   * having typed, matching the text the input already displays.
   */
  protected override get filterQuery(): string {
    return this.value
  }

  /** The typed query an inline ghost is composed against (the committed value). */
  private get inlineQuery(): string {
    return this.value
  }

  /**
   * Whether an inline ghost suffix is currently showing: only in `inline` mode,
   * when the active option (reached via the keyboard) has a label that starts
   * with the typed query and extends it.
   */
  private get hasInlineGhost(): boolean {
    if (!this.inline || !this.keyboardActive || !this.open) {
      return false
    }

    const option = this.activeOption
    const query = this.inlineQuery

    if (!option || !query) {
      return false
    }

    const label = option.label
    return (
      label.length > query.length
      && label.toLocaleLowerCase().startsWith(query.toLocaleLowerCase())
    )
  }

  /** The text shown in the input. */
  protected override get displayValue(): string {
    // While an inline ghost is showing, present the typed query plus the
    // highlighted option's remaining label (with the original casing).
    if (this.hasInlineGhost) {
      const query = this.inlineQuery
      const { label } = this.activeOption!
      return query + label.slice(query.length)
    }

    // Otherwise the input always reflects the committed free-text value.
    return this.value
  }

  /** Free text is always present once non-empty (drives the clear affordance). */
  protected override get hasValue(): boolean {
    return this.value.length > 0
  }

  protected override get formValue(): string | undefined {
    return this.value || undefined
  }

  protected override get showClear(): boolean {
    return this.clearable && this.hasValue && !this.disabled && !this.readonly
  }

  /**
   * Autocomplete is a search input with suggestions, not a picker — like Base
   * UI's autocomplete it renders no dropdown chevron (its `AriaCombobox` has no
   * `Trigger`). The list appears as you type, not on a toggle click.
   */
  protected override get showToggle(): boolean {
    return false
  }

  /**
   * The `aria-autocomplete` value for the inner input, mapping the `inline` +
   * `external-filtering` axes onto the four ARIA values:
   * - inline + internal filter → `both`
   * - inline + external-filtering → `inline` (static list + ghost)
   * - external-filtering (no inline) → `none` (static list, no ghost)
   * - otherwise → `list` (internal filter, no ghost)
   */
  protected override get ariaAutocomplete(): 'list' | 'both' | 'inline' | 'none' {
    if (this.inline) {
      return this.externalFiltering ? 'inline' : 'both'
    }
    return this.externalFiltering ? 'none' : 'list'
  }

  /**
   * Autocomplete renders no selection state — `aria-selected` is omitted
   * (`optionAriaSelected` → nothing) and the check is never shown
   * (`showCheck` → false) — so nothing is ever "selected".
   */
  protected override isSelected(): boolean {
    return false
  }

  /**
   * Autocomplete has no selection model, so the listbox does not advertise
   * `aria-multiselectable` at all (the base's `'false'` would imply a single-
   * select semantic it doesn't have).
   */
  protected override get ariaMultiselectable(): undefined {
    return undefined
  }

  /** Autocomplete never shows the selected-check icon (it is not a select). */
  protected override showCheck(): boolean {
    return false
  }

  /** No selection model, so no leading check gutter (avoids indenting suggestions). */
  protected override get rendersCheck(): boolean {
    return false
  }

  /** Autocomplete options are completion aids, not selectable values — omit aria-selected. */
  protected override optionAriaSelected(): typeof nothing {
    return nothing
  }

  /** Autocomplete keeps its committed free-text query when the listbox closes. */
  protected override onHide() {
    // Intentionally empty: the free-text value is the query, so it is not
    // discarded the way select mode's transient filter is.
  }

  /** Typing commits the typed text as the live value. */
  protected override onInput(input: HTMLInputElement): void {
    // Typing breaks any inline ghost suffix the keyboard had composed.
    this.keyboardActive = false
    // The typed text *is* the value, so it tracks the input live. `input` has
    // already fired (in the base); `commit()` emits the live `change`.
    this.value = input.value
    this.commit()
  }

  /**
   * Runs before the base's own key switch. Marks keyboard navigation (so the
   * inline ghost may compose) and owns the ArrowRight/End inline-accept. After
   * an accept the listbox is closed, so the base's own `End` branch (gated on
   * `this.open`) is skipped; the base still performs the moves for the arrows
   * and Home.
   */
  protected override onKeydown(e: KeyboardEvent): void {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Home':
        // The base performs the actual move (and opens a closed listbox); mark
        // it as keyboard navigation so the ghost composes after it.
        if (this.open) {
          this.keyboardActive = true
        }
        break
      case 'ArrowRight':
      case 'End':
        // Accept an inline ghost suffix: End always sits at the text end, and
        // ArrowRight accepts when the selection already reaches the end. When the
        // ghost suffix is selected, `selectionEnd` is the text length, so both
        // cases collapse to "is the selection at the end?". A bare `End` (no
        // ghost) falls through to the base's jump-to-last navigation, which we
        // mark as keyboard nav so the ghost composes for the newly-active row.
        if (this.inline && this.open && this.hasInlineGhost) {
          const input = this.inputRef.value
          const atEnd = !input || input.selectionEnd === input.value.length
          if (atEnd) {
            e.preventDefault()
            this.acceptInline()
            break
          }
        }
        if (e.key === 'End' && this.open) {
          this.keyboardActive = true
        }
        break
      case 'Backspace':
      case 'Delete':
        // A deletion breaks any inline ghost suffix.
        this.keyboardActive = false
        break
    }
  }

  protected override onMove(): void {
    this.keyboardActive = true
  }

  /**
   * Fully owns Enter when the listbox is open. An inline ghost or any
   * highlighted option fills+commits its label; otherwise the typed text is
   * already committed, so Enter just closes and does NOT preventDefault (the
   * owning form may submit). Returns `true` so the base does no further handling.
   */
  protected override handleEnter(e: KeyboardEvent): boolean {
    if (this.inline && this.hasInlineGhost && this.activeOption) {
      e.preventDefault()
      this.acceptInline()
      return true
    }

    // A navigated (arrow-key) highlight always commits. `autoHighlight` only
    // implicitly commits the first row when there is a query to complete — Base
    // UI does not highlight index 0 on an empty query, so a bare Enter with no
    // typed text just closes (and may submit the form).
    if ((this.navigated || (this.autoHighlight && this.hasQuery)) && this.activeOption) {
      e.preventDefault()
      this.selectOption(this.activeOption)
      return true
    }

    this.hide()
    return true
  }

  /** Whether the user has typed/committed any free text to complete against. */
  private get hasQuery(): boolean {
    return this.filterQuery.length > 0
  }

  /** Pointer hover never composes an inline ghost (mirrors keyboard-vs-pointer). */
  protected override onOptionHover(): void {
    this.keyboardActive = false
  }

  /**
   * Never silently commit an un-accepted inline suggestion: on blur drop the
   * ghost suffix and restore the input to the typed query (the actual value).
   */
  protected override onBlur(): void {
    if (this.inline && this.hasInlineGhost) {
      this.revertInline()
    }
    this.keyboardActive = false
  }

  /**
   * Lit binds the inner input's `.value` to the ghost-composed string, but the
   * suffix selection has to be applied imperatively after the DOM update so
   * continued typing overwrites it.
   */
  protected override afterUpdate(): void {
    if (this.hasInlineGhost) {
      const input = this.inputRef.value
      if (input) {
        input.setSelectionRange(this.inlineQuery.length, input.value.length)
      }
    }
  }

  /**
   * Picking a suggestion is a completion aid: fill the input with its label and
   * commit that label as the free-text value. No chips, no array, no toggle.
   */
  protected override selectOption(option: ComboboxOption): void {
    if (option.disabled) {
      return
    }

    // Remember the label so it survives the option leaving the result set.
    this.labelCache.set(option.value, option.label)

    this.keyboardActive = false
    this.value = option.label
    this.search = option.label
    this.commit()
    this.hide()
    // Restore focus to the input. Focus alone never opens the listbox.
    this.focus()
  }

  /** Commit the currently ghost-highlighted option's full label (inline mode). */
  private acceptInline() {
    const option = this.activeOption
    if (!option) {
      return
    }
    this.selectOption(option)
  }

  /**
   * Drop the transient inline ghost suffix, restoring the input to the typed
   * query (the actual committed `value`). Does not change `value`.
   */
  private revertInline() {
    this.keyboardActive = false

    const input = this.inputRef.value
    if (input) {
      input.value = this.value
      // Collapse any lingering suffix selection to the end of the typed text.
      input.setSelectionRange(this.value.length, this.value.length)
    }
  }

  /** A new query supersedes any inline ghost from the previous one. */
  protected override resetActiveIndex() {
    super.resetActiveIndex()
    this.keyboardActive = false
  }

  /** Clear the current value, firing `clear` then `change`. */
  override clear() {
    this.value = ''
    this.search = ''
    this.keyboardActive = false
    this.dispatchEvent(new ComboboxClearEvent())
    this.commit()
    this.focus()
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-autocomplete': Autocomplete
  }
}
